import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * The JS mirror of the motion tokens (spec 07 › Movimiento). `globals.css` is
 * the source and `motion-tokens.test.ts` keeps both in step. This module is in
 * the boot, so it stays small: the recipe table and the class contract live
 * in `motion-recipes.ts`, which only the tests, the guards and the lab read.
 */
export const MOTION = {
  /** Enter durations (`--duration-*`). */
  ms: {
    press: 60,
    instant: 80,
    fast: 120,
    base: 200,
    moderate: 240,
    slow: 320,
    push: 280,
    pop: 240,
    signature: 480,
    wash: 900,
    hold: 1600,
    loop: 1600,
  },
  /** Exits, about two thirds of their enter (`--duration-*-exit`). */
  exit: { fast: 80, base: 130, moderate: 160, slow: 210 },
  delay: { indicator: 150, tooltip: 300, tooltipWarm: 300 },
  stagger: { row: 30, rowCap: 4, block: 40, blockCap: 2, digit: 16, budget: 120 },
  /** The cubics of `--ease-*`: the springs' fallback, and the curves the `t95` test computes with. */
  ease: {
    enter: [0.16, 1, 0.3, 1],
    exit: [0.4, 0, 1, 1],
    move: [0.32, 0.6, 0.14, 1],
    settle: [0.46, 1.06, 0.16, 1],
    sheet: [0.32, 0.72, 0, 1],
    inOut: [0.4, 0, 0.2, 1],
  },
  hot: { window: 400, release: 600 },
  arm: { ms: 400 },
  stream: { maxLagMs: 100, flushMs: 300 },
  work: { stallAfter: 20_000, settleAfter: 30_000, beatMinGap: 2_000, calmWindow: 60_000, teachMax: 3 },
  gesture: { axisLockPx: 8, velocityWindowMs: 100, staleSampleMs: 50, projectionMs: 200, rubber: { c: 0.55, dim: 120 } },
  quiet: { maxConcurrent: 6, flipAfterSettle: 400 },
  momentFree: { idleMs: 1_000 },
} as const;

export type MotionMs = keyof typeof MOTION.ms;
export type MotionExit = keyof typeof MOTION.exit;
export type MotionEase = keyof typeof MOTION.ease;
/** A duration token as CSS names it: an enter (`base`) or an exit (`base-exit`). */
export type MotionDuration = MotionMs | `${MotionExit}-exit`;

export function motionMs(token: MotionDuration): number {
  return token.endsWith("-exit") ? MOTION.exit[token.slice(0, -5) as MotionExit] : MOTION.ms[token as MotionMs];
}

const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";
let reducedSource: unknown;
let reducedList: MediaQueryList | null = null;

/** One list for the page; built again only when `window.matchMedia` itself is replaced (tests stub it). */
function reducedMotionList(): MediaQueryList | null {
  const match = typeof window === "undefined" ? undefined : window.matchMedia;
  if (match !== reducedSource) {
    reducedSource = match;
    try {
      reducedList = window.matchMedia(REDUCED_QUERY);
    } catch {
      reducedList = null;
    }
  }
  return reducedList;
}

/**
 * The person asked to reduce motion. Unknown (no `matchMedia`, it throws, a
 * partial mock) reads as reduced: nothing moves when we cannot ask.
 */
export function prefersReducedMotion(): boolean {
  return reducedMotionList()?.matches ?? true;
}

function subscribeReducedMotion(onChange: () => void): () => void {
  const list = reducedMotionList();
  // Mocks are often partial ({ matches, media }) and Safari < 14 only has addListener.
  if (list?.addEventListener) list.addEventListener("change", onChange);
  else list?.addListener?.(onChange);
  return () => {
    if (list?.removeEventListener) list.removeEventListener("change", onChange);
    else list?.removeListener?.(onChange);
  };
}

/**
 * `prefersReducedMotion()` as state. The server snapshot is `true`
 * (pre-rendered pages and `renderToString` never animate); no markup may
 * depend on it, only what moves.
 */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, prefersReducedMotion, () => true);
}

const easings: Partial<Record<MotionEase, string>> = {};

/**
 * A curve for `element.animate()`, which cannot resolve `var()`: the value of
 * `--ease-*` at :root (the springs are `linear()` where the engine has it).
 * An empty read is never cached: the private stylesheet loads without
 * blocking, and until it lands the cubic of `MOTION.ease` stands in.
 */
export function cssEasing(name: MotionEase): string {
  let value = easings[name];
  if (!value) {
    try {
      value = getComputedStyle(document.documentElement)
        .getPropertyValue(`--ease-${name === "inOut" ? "in-out" : name}`)
        .trim();
    } catch {
      // No document (SSR, node tests): the cubic below.
    }
    if (!value) return `cubic-bezier(${MOTION.ease[name].join(", ")})`;
    easings[name] = value;
  }
  return value;
}

let linearCurves: boolean | undefined;

/** The engine takes `linear()` curves (a generated one must not void a whole `transition` in Safari 16.4–17.1). */
export function supportsLinear(): boolean {
  if (linearCurves === undefined) {
    try {
      linearCurves = CSS.supports("transition-timing-function", "linear(0, 1)");
    } catch {
      linearCurves = false;
    }
  }
  return linearCurves;
}

export interface MotionAnimateOptions {
  ms: MotionDuration;
  ease?: MotionEase | "linear";
  delay?: number;
  /**
   * `backwards` by default: the first keyframe covers the delay and nothing
   * stays behind, so the keyframes end on the element's own state (an enter, a
   * FLIP). `forwards` or `both` hold the last keyframe, written inline where
   * nothing animates: only for an exit whose element is removed afterwards. A
   * held animation beats every later class, transition and inline style; an
   * end state that must stay is committed (`commitStyles` + `cancel`).
   */
  fill?: FillMode;
}

/**
 * WAAPI with the tokens. With reduced motion, keyframes that fade keep only
 * their opacity, in ≤ 120 ms and linear, faded in place; keyframes without
 * opacity (a FLIP, a slide) do not animate, so nothing flashes from 0, and
 * nothing does without `element.animate` (jsdom). With a held fill, what did
 * not animate lands on the last keyframe: at once, or as the reduced fade
 * ends (not when it is cancelled: someone took over). `null` when nothing
 * runs.
 */
export function motionAnimate(
  el: Element,
  frames: Keyframe[],
  { ms, ease = "enter", delay = 0, fill = "backwards" }: MotionAnimateOptions,
): Animation | null {
  const reduce = prefersReducedMotion();
  const { offset: _offset, easing: _easing, composite: _composite, ...end } = frames.at(-1) ?? {};
  // Without a held fill the end state is the element's own: nothing to write.
  const land = (values: Keyframe) => {
    if ((fill === "both" || fill === "forwards") && "style" in el) Object.assign((el as HTMLElement).style, values);
  };
  const fades = frames.filter((frame) => frame.opacity != null);
  if (typeof el.animate === "function" && (!reduce || fades.length > 0)) {
    try {
      const animation = el.animate(reduce ? fades.map(({ opacity, offset }) => ({ opacity, offset })) : frames, {
        duration: reduce ? Math.min(motionMs(ms), MOTION.ms.fast) : motionMs(ms),
        easing: reduce || ease === "linear" ? "linear" : cssEasing(ease),
        delay,
        fill,
      });
      if (reduce) {
        const { opacity: _opacity, ...rest } = end;
        void animation.finished?.then(() => land(rest), () => {});
      }
      return animation;
    } catch {
      // A keyframe or curve this engine rejects: land on the end state.
    }
  }
  land(end);
  return null;
}

/** The delay of item `i` in a first reveal: capped (rows at 4, blocks at 2), 0 with reduced motion. */
export function staggerDelay(i: number, kind: "row" | "block"): number {
  if (prefersReducedMotion()) return 0;
  return Math.min(Math.max(i, 0), MOTION.stagger[`${kind}Cap`]) * MOTION.stagger[kind];
}

/** One heartbeat: the negative delay that puts a loop in phase with `document.timeline`. */
export function loopPhase(period: number = MOTION.ms.loop): string {
  const now = Number(typeof document === "undefined" ? 0 : document.timeline?.currentTime);
  return Number.isFinite(now) ? `${-Math.round(now % period)}ms` : "0ms";
}

/**
 * A ref for the element that carries a loop (`animate-breathe`,
 * `animate-sweep`…): it writes `animation-delay: loopPhase()` in the commit
 * where the element mounts and again when `on` turns true (the moment its loop
 * class goes on), so every loop on screen breathes on one heartbeat and a
 * relay never restarts the light. Never read in a render.
 */
export function useLoopPhase(on = true, period?: number): (el: HTMLElement | SVGElement | null) => void {
  return useCallback(
    (el: HTMLElement | SVGElement | null) => {
      if (el && on) el.style.animationDelay = loopPhase(period);
    },
    [on, period],
  );
}

/**
 * `<html data-motion-ready>` once the tree has committed and painted (after
 * hydration on the public pages): the entrances (`enter-*`, gated on
 * `:root[data-motion-ready]`) run only from then on, so pre-rendered markup
 * and a first client render never animate on load. Two frames: what the first
 * commit inserted takes its first style before the gate opens, so its
 * @starting-style never plays.
 */
export function useMotionReady(): void {
  useEffect(() => {
    const open = () => {
      document.documentElement.dataset.motionReady = "";
    };
    if (typeof requestAnimationFrame !== "function") return open();
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(open);
    });
    return () => cancelAnimationFrame(frame);
  }, []);
}

export interface WhenAnimationsDoneOptions {
  /** The longest wait; by default the end of the longest running animation plus 100 ms. */
  fallbackMs?: number;
  /** Aborting drops the wait and the promise never settles (a ghost that reopened is not removed). */
  signal?: AbortSignal;
  /** Also wait for the descendants' animations. */
  subtree?: boolean;
}

function remainingMs(animation: Animation): number {
  const timing = animation.effect?.getComputedTiming();
  return Math.max(0, Number(timing?.endTime ?? 0) - Number(timing?.localTime ?? 0));
}

/**
 * The end of a ghost (an exit, a swap, a wash): the one way to wait for an
 * element's animations (spec 07 › Movimiento, P6). Resolves at once when
 * nothing runs, because hot mode, `display: none` and 0 ms durations never
 * fire an event; otherwise when every finite animation finishes or is
 * cancelled, or at the fallback (a net that waits for an exit a long task
 * started late). Listing the animations flushes styles: call
 * it after the class that animates is on the element. Without
 * `getAnimations` (jsdom), the computed `animation-name` decides.
 */
export function whenAnimationsDone(
  el: Element | null,
  { fallbackMs, signal, subtree }: WhenAnimationsDoneOptions = {},
): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return;
    let running: Animation[] | undefined;
    if (el && typeof el.getAnimations === "function") {
      // Loops never end: they are not waited for.
      running = el.getAnimations({ subtree }).filter((a) => a.effect?.getTiming().iterations !== Infinity);
      if (running.length === 0) return resolve();
    } else {
      const name = el && typeof getComputedStyle === "function" ? getComputedStyle(el).animationName : "";
      if (!name || name === "none") return resolve();
    }
    const target = el as Element;
    const onEnd = (event: Event) => {
      if (event.target === target) finish();
    };
    const stop = () => {
      window.clearTimeout(timer);
      target.removeEventListener("animationend", onEnd);
      target.removeEventListener("animationcancel", onEnd);
      signal?.removeEventListener("abort", stop);
    };
    const finish = () => {
      stop();
      if (!signal?.aborted) resolve();
    };
    // The fallback is a net on the wall clock and the animations run on frames:
    // a long task after the commit (a detail that renders) holds their first
    // frame, and the net let a ghost go before its exit showed (the action
    // bar's placeholder vanished in one frame). While the page is visible and
    // one still plays, the net waits for its rest by the frame clock (a few
    // times at most); a hidden tab, a paused or a listed-less one end at the net.
    let timer = 0;
    let rearms = 0;
    const net = (ms: number) => {
      timer = window.setTimeout(() => {
        const playing = running?.filter((a) => a.playState === "running") ?? [];
        if (playing.length > 0 && rearms++ < 4 && document.visibilityState === "visible") {
          net(Math.max(...playing.map(remainingMs)) + 100);
        } else finish();
      }, ms);
    };
    net(fallbackMs ?? (running ? Math.max(...running.map(remainingMs)) : MOTION.exit.slow) + 100);
    signal?.addEventListener("abort", stop, { once: true });
    if (running) void Promise.allSettled(running.map((a) => a.finished)).then(finish);
    else {
      target.addEventListener("animationend", onEnd);
      target.addEventListener("animationcancel", onEnd);
    }
  });
}

/**
 * The remote switch (`config/global.motionOff`): the kinds turned off without
 * a deploy (a VT kind or a recipe of `MOTION_RECIPES`), on
 * `<html data-motion-off="theme push">` so CSS can read
 * `:root[data-motion-off~="flip"]`.
 */
export function applyMotionOff(kinds: readonly string[]): void {
  if (typeof document === "undefined") return;
  const off = kinds.filter((kind) => /^[a-z][\w-]*$/i.test(kind)).join(" ");
  if (off) document.documentElement.dataset.motionOff = off;
  else delete document.documentElement.dataset.motionOff;
}

export function isMotionOff(kind: string): boolean {
  const off = typeof document === "undefined" ? undefined : document.documentElement.dataset.motionOff;
  return !!off && ` ${off} `.includes(` ${kind} `);
}

let liteDevice: boolean | undefined;

/**
 * A modest device (≤ 2 GB of memory or ≤ 2 cores), read once: the theme
 * swaps in the tap with no reveal, list FLIPs and the grade wave become
 * crossfades there. Not ≤ 4 cores: WebKit reports 4 on every iPhone.
 */
export function isLiteDevice(): boolean {
  if (liteDevice === undefined) {
    const nav = typeof navigator === "undefined" ? undefined : (navigator as Navigator & { deviceMemory?: number | null });
    // An unreported value (no `deviceMemory` outside Chromium, a 0 or null from a privacy shim) is not modest.
    liteDevice = !!nav && ((nav.deviceMemory || Infinity) <= 2 || (nav.hardwareConcurrency || Infinity) <= 2);
  }
  return liteDevice;
}

let hotAt = Number.NEGATIVE_INFINITY;
let hotRelease = 0;

/**
 * Hot mode (spec 07 › Movimiento › Modo caliente): the app gets faster the
 * faster the person goes. A held key (`repeat`), or a second hot action
 * within 400 ms of the last, puts `<html data-motion="hot">`, written once
 * per burst; it goes 600 ms after the last action, and while it is on every
 * action counts. The first action is never hot. Then what a burst repeats
 * lands at once (globals.css › hot mode: Swap, ink, digits, a leaving row,
 * the grade, staggers; FLIP, previous / next and AnimatedNumber read
 * `isHot()`), never a press, the focus, a progress, a wash, the arming or an
 * announcement. Called by the shortcuts (J/K, ↑/↓, E, S, Z), previous / next
 * and «Pasar», a tab, a segment, a chip, a copy and a tap that ends a View
 * Transition.
 */
export function noteHotAction(e?: { repeat?: boolean }): void {
  if (typeof document === "undefined") return;
  const now = performance.now();
  const since = now - hotAt;
  const burst = !!e?.repeat || (since >= 0 && since <= MOTION.hot.window) || isHot();
  hotAt = now;
  if (!burst) return;
  const root = document.documentElement;
  if (!isHot()) root.dataset.motion = "hot";
  window.clearTimeout(hotRelease);
  hotRelease = window.setTimeout(releaseHot, MOTION.hot.release);
}

/**
 * Takes hot mode off, and nothing replays (spec 07 › Modo caliente). A class
 * mounted in the burst's last few hundred ms ran in 0 s but keeps its start
 * time: restoring its duration makes it current again, and it would play its
 * tail (or hold its first keyframe through a delay). It cannot be finished
 * first: finished, it is not in `getAnimations()`. So what the removal
 * revives (running now, not before) lands at its end, in the same task,
 * before a frame paints. What was already running (a wash, a sheet, a loop)
 * is left alone.
 */
function releaseHot(): void {
  if (!isHot()) return;
  const running = new Set(document.getAnimations?.().filter((a) => a.playState === "running"));
  delete document.documentElement.dataset.motion;
  for (const animation of document.getAnimations?.() ?? []) {
    const revived = "animationName" in animation && animation.playState === "running" && !running.has(animation);
    if (revived && animation.effect?.getTiming().iterations !== Infinity) animation.finish();
  }
}

/** A burst is on (`<html data-motion="hot">`): what it repeats changes at once. */
export function isHot(): boolean {
  return typeof document !== "undefined" && document.documentElement.dataset.motion === "hot";
}
